import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, delay, EMPTY, map, Observable, of } from 'rxjs';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';

import { EventInfo, FormData } from './data.model';
import { INITIAL_DATA } from './data.initial';
import { environment } from 'src/environments/environment';
import { NavigationService } from './navigation.service';
import { SKIP_SPINNER_DELAY } from './interceptor.service';
import { BrandService } from './brand.service';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private formDataSubject = new BehaviorSubject<FormData>(INITIAL_DATA);
  formData$ = this.formDataSubject.asObservable();

  id: string;
  stepsData: FormData = INITIAL_DATA;
  resumeBooking: boolean = false;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private navigationService: NavigationService,
    private brandService: BrandService
  ) {
    this.route.queryParams.subscribe((params) => {
      if (Object.keys(params).length > 0) {
        if (params['id']) {
          this.id = params['id'];
          if (params['resume']) {
            this.resumeBooking = !!params['resume'];
          }
        }
      }
    });
  }

  getFormData(): FormData {
    return this.formDataSubject.value;
  }

  get username() {
    return this.stepsData?.appointmentRequest?.customer?.firstName || '';
  }

  saveStepsData(newData: FormData, skipSpinnerDelay: boolean = false) {
    const context = new HttpContext().set(SKIP_SPINNER_DELAY, skipSpinnerDelay);
    newData.appointmentRequest.brand = this.brandService.getCurrentBrandKey();
    if(this.stepsData.status === newData.status) newData.status = undefined;
    newData.id = this.id || '';
    return this.http
      .put<any>(`${environment.api_url}/Appointments`, newData, {context})
      .pipe(
        map((data: any) => {
          this.stepsData = newData;
          this.id = data.id;
          return {
            id: data.id,
            data: newData,
          };
        })
      );
  }

  getStepsData(id) {
    return this.http.get<any>(`${environment.api_url}/Appointments/${id}`).pipe(
      map((data: FormData) => {
        this.stepsData = data;
        this.id = id;
        if (this.resumeBooking) {
          this.redirectToTheLastPage();
        }
        return data;
      }),
      catchError((error: HttpErrorResponse) => {
        if(error.status === 404){
          this.navigationService.navigateToPath('', { id: null });
          this.id = '';
        }
        return EMPTY;
      })
    );
  }

  resetStepsData() {
    this.stepsData = INITIAL_DATA;
    this.id = null;
  }

  redirectToTheLastPage() {
    this.resumeBooking = false;
    const sessionData = JSON.parse(this.stepsData.sessionData);
    if (sessionData.nextPage) {
      this.navigationService.navigateToPath(
        sessionData.nextPage,
        {
          resume: null,
        }
      );
    }
  }

  getTimeBookingData(date: string, code: string, numberOfDays: number, isEvent: boolean): Observable<any> {
    const brand = this.brandService.getCurrentBrandKey();
    return this.http.get<any>(
      `${environment.api_url}/appointmentblocks?postalCode=${code}&startDate=${date}&numberOfDays=${numberOfDays}&isEvent=${isEvent}&brand=${brand}`
    );
  }

  getReviews(): Observable<any> {
    return this.http.post(
      `${environment.birdeye_url}/resources/v1/review/businessId/${environment.birdeye_business_id}?api_key=${environment.birdeye_api_key}&rating=5&count=50&max=50`,
      { sources: ['google'] }
    );
  }

  getReviewSummary(): Observable<any> {
    return this.http.get(
      `${environment.birdeye_url}/resources/v1/review/businessid/${environment.birdeye_business_id}/summary?api_key=${environment.birdeye_api_key}`
    );
  }

  propertyValidation(address?: {
    address: string,
    postalCode: string
  }): Observable<any> {
    const data = this.stepsData;
    const brand = this.brandService.getCurrentBrandKey();
    //const addressToValidate = address ? address : `${data.appointmentRequest.property.street1} ${data.appointmentRequest.property.street2} ${data.appointmentRequest.property.city} ${data.appointmentRequest.property.state} ${data.appointmentRequest.property.zipcode}`
    return this.http.post(`${environment.api_url}/PropertyValidation`, {
      address: address?.address || `${data.appointmentRequest.property.street1} ${data.appointmentRequest.property.street2} ${data.appointmentRequest.property.city} ${data.appointmentRequest.property.state} ${data.appointmentRequest.property.zipcode}`,
      firstName: data.appointmentRequest.customer.firstName,
      lastName: data.appointmentRequest.customer.lastName,
      postalCode: address?.postalCode || data.appointmentRequest.property.zipcode,
      brand
    });
  }

  serviceAvailability(zipcode?: string): Observable<any> {
    const data = this.stepsData;
    const zip = zipcode || data.appointmentRequest.property.zipcode;
    const brand = this.brandService.getCurrentBrandKey();
    return this.http.get(
      `${environment.api_url}/service-availability/${zip}/${brand}`
    );
  }

  getEventInfo(eventCampaignId: string): Observable<{campaign: EventInfo}> {
    return this.http.get<{campaign: EventInfo}>(
      `${environment.api_url}/Campaigns/${eventCampaignId}`
    );
  }

  getPromotionalOffers(): Observable<string> {
    return this.http.get<string>(
      `${environment.api_url}/PromotionalOffers`, { responseType: 'text' as 'json' }
    );
  }
  getPropertyOwners(address: any): Observable<any> {
    const addressToValidate = `${address.streetAddress1} ${address.streetAddress2} ${address.city} ${address.state} ${address.zipcode}`;
    return this.http.get<any>(
      `${environment.api_url}/PropertyOwners/${addressToValidate}`
    );
  }
}
